import PropTypes from 'prop-types';
import { Navigate } from 'react-router-dom';

// project-imports
import useAuth from 'hooks/useAuth';
import { APP_DEFAULT_PATH } from 'config';

// ==============================|| INCENTIVE GUARD ||============================== //
// Staff, Store Incentives and Traders Program access is a per-user toggle per page
// (Users → Advanced Features), not a role. `section` is one page:
//   clusterDashboard | clusterTargets | slabDashboard | slabTargets
//   storeDashboard | storeTargetUpload | storeAssignedStaffs
//   tradersDashboard | tradersRedemptions | tradersRcMapping | tradersApprovals
// or a group of them: 'staff' (any of the four Staff Incentives pages),
// 'store' (any of the three Store Incentives pages), 'traders' (any of the four
// Traders Program pages), 'any' (all eleven).
// Super admins always pass. Waits for auth to initialise so it never redirects
// before the user is known.

export const hasIncentiveAccess = (user, section = 'any') => {
  if (!user) return false;
  if (user.role === 'SUPER_ADMIN') return true;
  const f = {
    clusterDashboard: Boolean(user.clusterwiseDashboard?.is_clusterwise_dashboard),
    clusterTargets: Boolean(user.clusterwiseTargets?.is_clusterwise_targets),
    slabDashboard: Boolean(user.slabwiseDashboard?.is_slabwise_dashboard),
    slabTargets: Boolean(user.slabwiseTargets?.is_slabwise_targets),
    storeDashboard: Boolean(user.storeIncentivesDashboard?.is_store_dashboard),
    storeTargetUpload: Boolean(user.storeIncentivesTargetUpload?.is_store_target_upload),
    storeAssignedStaffs: Boolean(user.storeIncentivesAssignedStaffs?.is_store_assigned_staffs),
    tradersDashboard: Boolean(user.tradersDashboard?.is_traders_dashboard),
    tradersRedemptions: Boolean(user.tradersRedemptions?.is_traders_redemptions),
    tradersRcMapping: Boolean(user.tradersRcMapping?.is_traders_rc_mapping),
    tradersApprovals: Boolean(user.tradersApprovals?.is_traders_approvals),
  };
  const groups = {
    staff: ['clusterDashboard', 'clusterTargets', 'slabDashboard', 'slabTargets'],
    store: ['storeDashboard', 'storeTargetUpload', 'storeAssignedStaffs'],
    traders: ['tradersDashboard', 'tradersRedemptions', 'tradersRcMapping', 'tradersApprovals'],
    any: Object.keys(f),
  };
  if (groups[section]) return groups[section].some((k) => f[k]);
  return Boolean(f[section]);
};

// Pages in sidebar order, with the section that unlocks each — used to pick a
// landing page for users whose menu is made only of toggled pages.
const LANDING_PAGES = [
  ['tradersDashboard', '/traders/dashboard'],
  ['tradersRedemptions', '/traders/incentive-redemptions'],
  ['tradersRcMapping', '/traders/rc-mapping'],
  ['tradersApprovals', '/traders/approvals'],
  ['storeDashboard', '/store-incentives/dashboard'],
  ['storeTargetUpload', '/store-incentives/target-upload'],
  ['storeAssignedStaffs', '/store-incentives/assigned-staffs'],
  ['clusterDashboard', '/staff-incentives/clusterwise/dashboard'],
  ['clusterTargets', '/staff-incentives/clusterwise/targets'],
  ['slabDashboard', '/staff-incentives/slabwise/dashboard'],
  ['slabTargets', '/staff-incentives/slabwise/targets'],
];

// Where a user should land after login / when sent "home". The accounts team's
// sidebar has no general Dashboard (only toggled pages), so they go to the first
// page they have enabled; everyone else gets the app default.
export const homePathFor = (user) => {
  if (user?.role === 'ACCOUNTS_TEAM') {
    const hit = LANDING_PAGES.find(([section]) => hasIncentiveAccess(user, section));
    if (hit) return hit[1];
  }
  return APP_DEFAULT_PATH;
};

const IncentiveGuard = ({ children, section = 'any' }) => {
  const { isInitialized, isLoggedIn, user } = useAuth();

  if (!isInitialized) return null;
  if (isLoggedIn && hasIncentiveAccess(user, section)) return children;
  return <Navigate to={homePathFor(user)} replace />;
};

IncentiveGuard.propTypes = {
  children: PropTypes.node,
  section: PropTypes.string,
};

export default IncentiveGuard;
